import React, {
  memo,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { FixedSizeList as List } from "react-window";
import {
  LEAVE_CALENDAR_ROW_HEIGHT,
} from "./leaveCalendar.constants";
import { LeaveCalendarUserRow } from "./LeaveCalendarUserRow";
import type { LeaveRecord, CalendarUser, PublicHoliday } from "./leaveCalendar.types";

export interface RowItemData {
  users: CalendarUser[];
  days: Date[];
  monthStart: Date;
  monthEnd: Date;
  leaves: LeaveRecord[];
  holidays: PublicHoliday[];
  cellWidth: number;
  dragPreview: {
    userId: string;
    startIdx: number;
    endIdx: number;
  } | null;
  onRowPointerDown: (userId: string, e: React.PointerEvent<HTMLDivElement>) => void;
  onLeaveClick: (leave: LeaveRecord) => void;
  saturdayOff: boolean;
  sundayOff: boolean;
  resolveLeaveTypeLabel?: (code: string) => string;
  highlightedLeaveId?: string | null;
}

const InnerRow = memo(function InnerRow({
  index,
  style,
  data,
}: any) {
  const user = data.users[index];
  if (!user) return null;
  const preview =
    data.dragPreview?.userId === user.id
      ? {
          startIdx: data.dragPreview.startIdx,
          endIdx: data.dragPreview.endIdx,
        }
      : null;

  return (
    <div style={style}>
      <LeaveCalendarUserRow
        user={user}
        days={data.days}
        monthStart={data.monthStart}
        monthEnd={data.monthEnd}
        leaves={data.leaves}
        holidays={data.holidays}
        cellWidth={data.cellWidth}
        dragPreview={preview}
        onRowPointerDown={(e) => data.onRowPointerDown(user.id, e)}
        onLeaveClick={data.onLeaveClick}
        saturdayOff={data.saturdayOff}
        sundayOff={data.sundayOff}
        resolveLeaveTypeLabel={data.resolveLeaveTypeLabel}
        highlightedLeaveId={data.highlightedLeaveId}
      />
    </div>
  );
});

export interface LeaveCalendarVirtualBodyProps {
  /** Viewport height for the list (vertical scroll inside List). */
  height: number;
  /** Timeline width = days.length * cellWidth (row content; same as day columns). */
  width: number;
  /** react-window outer width: timeline + vertical scrollbar reserve (matches date header). */
  listOuterWidth: number;
  /** Day column width (fluid from viewport). */
  cellWidth: number;
  users: CalendarUser[];
  days: Date[];
  monthStart: Date;
  monthEnd: Date;
  leaves: LeaveRecord[];
  holidays: PublicHoliday[];
  listRef: React.RefObject<any>;
  dragPreview: RowItemData["dragPreview"];
  onRowPointerDown: RowItemData["onRowPointerDown"];
  onLeaveClick: (leave: LeaveRecord) => void;
  onScroll: (scrollTop: number) => void;
  /** Report native vertical scrollbar width so the page can pad the header and widen the list outer (avoids inner horizontal scroll + column drift). */
  onVerticalScrollbarWidthChange?: (scrollbarWidthPx: number) => void;
  saturdayOff?: boolean;
  sundayOff?: boolean;
  resolveLeaveTypeLabel?: (code: string) => string;
  highlightedLeaveId?: string | null;
}

export const LeaveCalendarVirtualBody = memo(function LeaveCalendarVirtualBody({
  height,
  width,
  listOuterWidth,
  cellWidth,
  users,
  days,
  monthStart,
  monthEnd,
  leaves,
  holidays,
  listRef,
  dragPreview,
  onRowPointerDown,
  onLeaveClick,
  onScroll,
  onVerticalScrollbarWidthChange,
  saturdayOff = true,
  sundayOff = true,
  resolveLeaveTypeLabel,
  highlightedLeaveId,
}: LeaveCalendarVirtualBodyProps) {
  const { t } = useTranslation();
  const listOuterRef = useRef<HTMLDivElement | null>(null);

  const reportScrollbarWidth = useCallback(() => {
    const el = listOuterRef.current;
    if (!el || !onVerticalScrollbarWidthChange) return;
    const sw = el.offsetWidth - el.clientWidth;
    onVerticalScrollbarWidthChange(sw);
  }, [onVerticalScrollbarWidthChange]);

  useLayoutEffect(() => {
    if (users.length === 0) {
      onVerticalScrollbarWidthChange?.(0);
      return;
    }
    reportScrollbarWidth();
  }, [
    users.length,
    reportScrollbarWidth,
    onVerticalScrollbarWidthChange,
    height,
    listOuterWidth,
  ]);

  useLayoutEffect(() => {
    const el = listOuterRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => reportScrollbarWidth());
    ro.observe(el);
    return () => ro.disconnect();
  }, [reportScrollbarWidth]);

  const itemData = useMemo(
    () => ({
      users,
      days,
      monthStart,
      monthEnd,
      leaves,
      holidays,
      cellWidth,
      dragPreview,
      onRowPointerDown,
      onLeaveClick,
      saturdayOff,
      sundayOff,
      resolveLeaveTypeLabel,
      highlightedLeaveId,
    }),
    [
      users,
      days,
      monthStart,
      monthEnd,
      leaves,
      holidays,
      cellWidth,
      dragPreview,
      onRowPointerDown,
      onLeaveClick,
      saturdayOff,
      sundayOff,
      resolveLeaveTypeLabel,
      highlightedLeaveId,
    ]
  );

  const onListScroll = useCallback(
    (props: { scrollOffset: number }) => {
      onScroll(props.scrollOffset);
    },
    [onScroll]
  );

  if (height <= 0 || users.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-sm text-slate-500"
        style={{ height: Math.max(height, 120), width: listOuterWidth }}
      >
        {t("no_data")}
      </div>
    );
  }

  return (
    <List
      ref={listRef}
      height={height}
      width={listOuterWidth}
      itemCount={users.length}
      itemSize={LEAVE_CALENDAR_ROW_HEIGHT}
      itemData={itemData}
      onScroll={onListScroll}
      className="leave-calendar-vlist no-scrollbar"
      outerRef={(node: HTMLDivElement | null) => {
        listOuterRef.current = node;
      }}
      style={{
        overflow: "hidden auto",
      }}
    >
      {InnerRow}
    </List>
  );
});
